import { Outlet, useParams, useLocation, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { Plus, X } from '@/ui/icons'
import { PageLayout } from '@/ui/layout'
import { Sheet, SheetContent, SheetBody } from '@/ui/components'
import { Button } from '@/ui/components'
import { TripsSidebar, TripsSidebarContent } from './TripsSidebar'
import { TripDetailPanelProvider, useTripDetailPanel } from './contexts/TripDetailPanelContext'
import { TripsSheetProvider } from './contexts/TripsSheetContext'
import { TripDetailRightPanel } from './components/TripDetailRightPanel'
import { CrewChatWidget } from './components/CrewChatWidget'
import { useTripRealtimeSync } from '@/modules/trips/hooks/useTripRealtimeSync'
import { useCreateModal } from '@/contexts/CreateModalContext'
import { useAppNav } from '@/contexts/AppNavContext'
import { useRangerPanel } from '@/contexts/RangerPanelContext'
import { useMinWidth } from '@/shared/hooks/useMinWidth'
import { cn } from '@/shared/utils'

const MOBILE_BREAKPOINT = 767
const LEFT_PANEL_STORAGE_KEY = 'sitrepp.trips.leftPanelCollapsed'
/** Below this, the nav, Ranger and the trips list leave an open trip under about 540px, so the list collapses. */
const RANGER_WITH_TRIPS_LIST_MIN_WIDTH = { rail: 1256, expandedNav: 1440 }
/** Below this, Ranger and the trip's right-hand detail panel can't be open together. */
const RANGER_WITH_DETAIL_PANEL_MIN_WIDTH = { rail: 1600, expandedNav: 1780 }

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`)
    const update = () => setIsMobile(mql.matches)
    update()
    mql.addEventListener('change', update)
    return () => mql.removeEventListener('change', update)
  }, [])
  return isMobile
}

function TripsLayoutContent() {
  const { tripId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { selectedDetail, closePanel } = useTripDetailPanel()
  const isMobile = useIsMobile()
  const showRightPanel = Boolean(tripId && selectedDetail)

  // Always-on realtime sync: invalidates meal/gear caches whenever another client
  // mutates trip data, regardless of whether the right-side detail panel is open.
  useTripRealtimeSync(tripId)

  const [leftPanelCollapsed, setLeftPanelCollapsedState] = useState(() => {
    if (typeof window === 'undefined') return false
    try {
      return window.localStorage.getItem(LEFT_PANEL_STORAGE_KEY) === 'true'
    } catch {
      return false
    }
  })

  const [tripsSheetOpen, setTripsSheetOpen] = useState(false)
  const { openCreateModal } = useCreateModal()

  useEffect(() => {
    try {
      window.localStorage.setItem(LEFT_PANEL_STORAGE_KEY, String(leftPanelCollapsed))
    } catch {
      // ignore
    }
  }, [leftPanelCollapsed])

  useEffect(() => {
    setTripsSheetOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (location.pathname === '/trips/new') {
      openCreateModal()
      navigate('/trips', { replace: true })
    }
  }, [location.pathname, navigate, openCreateModal])

  const { isOpen: rangerOpen, closeRangerPanel } = useRangerPanel()
  const { expanded: navExpanded } = useAppNav()
  const fitsTripsListWithRanger = useMinWidth(
    navExpanded ? RANGER_WITH_TRIPS_LIST_MIN_WIDTH.expandedNav : RANGER_WITH_TRIPS_LIST_MIN_WIDTH.rail
  )
  const fitsDetailPanelWithRanger = useMinWidth(
    navExpanded ? RANGER_WITH_DETAIL_PANEL_MIN_WIDTH.expandedNav : RANGER_WITH_DETAIL_PANEL_MIN_WIDTH.rail
  )
  const rangerSqueezesTripsList = rangerOpen && !fitsTripsListWithRanger

  // When Ranger and the detail panel don't both fit, whichever the user just opened stays.
  // These run only when their own side changes, which is how each knows it's the newer one.
  useEffect(() => {
    if (rangerOpen && showRightPanel && !fitsDetailPanelWithRanger) closePanel()
  }, [rangerOpen])
  useEffect(() => {
    if (showRightPanel && rangerOpen && !fitsDetailPanelWithRanger) closeRangerPanel()
  }, [showRightPanel, fitsDetailPanelWithRanger])

  const toggleLeftPanel = () => {
    // The list is only collapsed for Ranger's sake here, so bringing it back means closing Ranger.
    if (rangerSqueezesTripsList) {
      closeRangerPanel()
      setLeftPanelCollapsedState(false)
      return
    }
    setLeftPanelCollapsedState((prev) => !prev)
  }
  const hasRightPanelSlot = Boolean(tripId)

  return (
    <>
    <div className="flex flex-col md:flex-row h-[100dvh] w-full overflow-hidden">
      <div className="md:hidden flex-shrink-0 flex items-center justify-between gap-2 px-4 py-3 border-b border-earth-200">
        <span className="text-lg font-semibold text-earth-900 dark:text-foreground">Trips</span>
        <Button
          variant="ghost"
          size="sm"
          className="gap-2 min-h-[44px]"
          onClick={openCreateModal}
          aria-label="Create trip"
        >
          <Plus className="h-5 w-5" />
          Create trip
        </Button>
      </div>

        <div className="flex flex-1 min-w-0 min-h-0 overflow-hidden">
          <TripsSidebar
            collapsed={leftPanelCollapsed || rangerSqueezesTripsList}
            onToggleCollapse={toggleLeftPanel}
          />

          <div className="flex-1 min-w-0 min-h-0 overflow-y-auto overscroll-contain">
            <TripsSheetProvider openTripsSheet={() => setTripsSheetOpen(true)}>
              {tripId ? (
                <Outlet />
              ) : (
                <>
                  <div className="md:hidden flex flex-col h-full min-h-0">
                    <TripsSidebarContent
                      headerRight={
                        <Button
                          size="sm"
                          className="h-8 w-auto min-h-[44px] gap-1.5 px-5 text-xs shrink-0"
                          onClick={openCreateModal}
                        >
                          <Plus className="h-4 w-4" />
                          Create trip
                        </Button>
                      }
                    />
                  </div>
                  <div className="hidden md:flex flex-1 min-w-0 min-h-0 items-center justify-center">
                    <Outlet />
                  </div>
                </>
              )}
            </TripsSheetProvider>
          </div>

          {hasRightPanelSlot && (
            <>
              <div
                className={cn(
                  'hidden md:block flex-shrink-0 transition-[width] duration-200 ease-out',
                  showRightPanel ? 'w-[384px]' : 'w-0 min-w-0 overflow-hidden'
                )}
                aria-hidden
              />
              {showRightPanel && (
                <div className="hidden md:block fixed top-4 right-4 bottom-4 z-[45] w-[min(360px,calc(100vw-10rem))] min-w-0">
                  <TripDetailRightPanel tripId={tripId!} />
                </div>
              )}
            </>
          )}

          {hasRightPanelSlot && (
            <Sheet open={showRightPanel && isMobile} onOpenChange={(open) => !open && closePanel()}>
              <SheetContent side="bottom" className="md:hidden p-0 gap-0 max-h-[90dvh] flex flex-col">
                <div className="flex-shrink-0 flex justify-center pt-2 pb-1" aria-hidden>
                  <div className="w-10 h-1 rounded-full bg-earth-300" />
                </div>
                <SheetBody className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-0">
                  <TripDetailRightPanel tripId={tripId!} variant="drawer" />
                </SheetBody>
              </SheetContent>
            </Sheet>
          )}
        </div>

        <Sheet open={tripsSheetOpen} onOpenChange={setTripsSheetOpen}>
          <SheetContent side="left" className="p-0 gap-0 w-full max-w-[320px]">
            <SheetBody className="p-0 flex flex-col h-full">
              <TripsSidebarContent
                headerRight={
                  <>
                    <Button
                      size="sm"
                      className="h-8 w-auto min-h-[44px] gap-1.5 px-5 text-xs shrink-0"
                      onClick={openCreateModal}
                    >
                      <Plus className="h-4 w-4" />
                      Create trip
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 min-h-[44px] min-w-[44px]"
                      onClick={() => setTripsSheetOpen(false)}
                      aria-label="Close"
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </>
                }
              />
            </SheetBody>
          </SheetContent>
        </Sheet>

    </div>

      {/* Persistent floating crew chat — only visible when a trip is selected */}
      {tripId && <CrewChatWidget tripId={tripId} />}
    </>
  )
}

export function TripsLayout() {
  return (
    <PageLayout>
      <TripDetailPanelProvider>
        <TripsLayoutContent />
      </TripDetailPanelProvider>
    </PageLayout>
  )
}
